import { create } from 'zustand';
import { BLIND_LAST_MOVE_MS } from '../constants/timing';

/** Quanto dura una sbirciata. Abbastanza per rileggere la posizione, non per studiarla. */
export const PEEK_MS = 2000;

interface BlindStore {
  /**
   * Istante in cui finisce la rivelazione in corso, `null` se non si sta sbirciando.
   * Un istante e non un booleano: chi lo legge arma il proprio timer sulla scadenza,
   * e una seconda sbirciata mentre la prima è accesa non lascia in giro un timer vecchio
   * che spegnerebbe la scacchiera prima del tempo.
   */
  peekUntilMs: number | null;
  /** Quante volte si è sbirciato in QUESTA partita. Solo per chi gioca: non esce di qui. */
  peekCount: number;
  peek: () => void;
  /** La rivelazione è scaduta. */
  endPeek: () => void;
  /**
   * Fino a quando resta annunciata l'ultima mossa. Stessa forma a scadenza di
   * `peekUntilMs`, e per la stessa ragione: due mosse a meno di cinque secondi l'una
   * dall'altra devono SPOSTARE la fine della finestra, non lasciare acceso il timer
   * della prima che spegnerebbe l'annuncio della seconda a metà.
   */
  announcedUntilMs: number | null;
  /** Una mossa è appena stata giocata: la si annuncia (e si riapre la finestra intera). */
  announceMove: () => void;
  /** La finestra dell'annuncio è scaduta. */
  endAnnounce: () => void;
  /** Nuova partita: si riparte senza sbirciate e senza annunci in sospeso. */
  resetBlindSession: () => void;
}

/**
 * Stato della modalità alla cieca, tenuto FUORI da `GameScreen` per la stessa ragione di
 * [focus-store]: lo schermo di gioco si smonta e rimonta a ogni rivincita, e con lui se ne
 * andrebbe il conto delle sbirciate a metà partita. Nessuna persistenza — vale per la
 * partita in corso, e `resetBlindSession` azzera tutto all'inizio di ognuna.
 *
 * Il conto delle sbirciate NON viaggia al server ed è visibile solo a chi ha sbirciato: è
 * un appoggio per allenarsi, non un dato di gara — nessun controllo lato client potrebbe
 * comunque impedire uno screenshot o un secondo dispositivo.
 */
export const useBlindStore = create<BlindStore>((set) => ({
  peekUntilMs: null,
  peekCount: 0,
  peek: () => set((s) => ({ peekUntilMs: Date.now() + PEEK_MS, peekCount: s.peekCount + 1 })),
  endPeek: () => set({ peekUntilMs: null }),

  announcedUntilMs: null,
  announceMove: () => set({ announcedUntilMs: Date.now() + BLIND_LAST_MOVE_MS }),
  endAnnounce: () => set({ announcedUntilMs: null }),

  resetBlindSession: () => set({ peekUntilMs: null, peekCount: 0, announcedUntilMs: null }),
}));
